import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import AdminSidebar from "../components/layout/AdminSidebar";
import {
  Scale,
  FileText,
  Gavel,
  AlertTriangle,
  Clock,
  Users,
  Plus,
  ChevronRight,
  ArrowLeft,
  RefreshCw,
  Shield,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Lock,
  Loader2,
  Sparkles
} from "lucide-react";

const LegalCompliancePage = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [cases, setCases] = useState([]);
  const [hearings, setHearings] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [showAiDraft, setShowAiDraft] = useState(false);
  const [aiDraft, setAiDraft] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  
  // Forms
  const [documentForm, setDocumentForm] = useState({
    title: "",
    category: "policies",
    version: "1.0",
    description: "",
    access_level: "public"
  });
  
  const [draftForm, setDraftForm] = useState({
    notice_type: "courtesy",
    owner_name: "",
    details: ""
  });

  const isAdmin = user?.role && ["admin", "board", "property_manager"].includes(user.role);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, docsRes, casesRes, hearingsRes, policiesRes] = await Promise.all([
        axios.get(`${API}/legal/dashboard`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
        axios.get(`${API}/legal/documents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/legal/compliance`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { cases: [] } })),
        axios.get(`${API}/legal/hearings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { hearings: [] } })),
        axios.get(`${API}/legal/policies`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { policies: [] } }))
      ]);
      setDashboard(dashRes.data);
      setDocuments(docsRes.data.documents || []);
      setCases(casesRes.data.cases || []);
      setHearings(hearingsRes.data.hearings || []);
      setPolicies(policiesRes.data.policies || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDocument = async () => {
    try {
      const params = new URLSearchParams(documentForm);
      await axios.post(`${API}/legal/documents?${params}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDocumentDialog(false);
      setDocumentForm({ title: "", category: "policies", version: "1.0", description: "", access_level: "public" });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to create document");
    }
  };

  const handleAiDraftNotice = async () => {
    setAiLoading(true);
    try {
      const params = new URLSearchParams(draftForm);
      const res = await axios.post(`${API}/legal/ai/draft-notice?${params}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiDraft(res.data.draft);
    } catch (error) {
      setAiDraft("Failed to generate draft. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      initial_notice: "bg-blue-100 text-blue-800",
      courtesy_notice: "bg-sky-100 text-sky-800",
      formal_violation: "bg-amber-100 text-amber-800",
      hearing_scheduled: "bg-purple-100 text-purple-800",
      hearing_decision: "bg-indigo-100 text-indigo-800",
      fine_assessed: "bg-red-100 text-red-800",
      appeal_filed: "bg-orange-100 text-orange-800",
      closed: "bg-green-100 text-green-800"
    };
    return colors[stage] || "bg-gray-100 text-gray-800";
  };

  const getAccessIcon = (level) => {
    if (level === "attorney_privileged") return <Lock className="w-4 h-4 text-red-500" />;
    if (level === "board_only") return <Shield className="w-4 h-4 text-amber-500" />;
    if (level === "owners_only") return <Users className="w-4 h-4 text-blue-500" />;
    return <BookOpen className="w-4 h-4 text-green-500" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const pageContent = (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="legal-compliance-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/80 rounded-xl flex items-center justify-center">
            <Scale className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-serif text-xl font-semibold">Legal & Compliance Center</h1>
            <p className="text-sm text-muted-foreground">Governing documents, compliance, and legal management</p>
          </div>
        </div>
        {isAdmin && (
          <Dialog open={showDocumentDialog} onOpenChange={setShowDocumentDialog}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" /> Add Document
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Governing Document</DialogTitle>
                <DialogDescription>Upload a new governing document to the library</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Document Title</Label>
                    <Input
                      value={documentForm.title}
                      onChange={(e) => setDocumentForm({ ...documentForm, title: e.target.value })}
                      placeholder="CC&Rs - Horse Mountain Ranch"
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={documentForm.category} onValueChange={(v) => setDocumentForm({ ...documentForm, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ccrs">CC&Rs</SelectItem>
                        <SelectItem value="bylaws">Bylaws</SelectItem>
                        <SelectItem value="policies">Policies & Procedures</SelectItem>
                        <SelectItem value="design_guidelines">Design Guidelines</SelectItem>
                        <SelectItem value="resolutions">Resolutions</SelectItem>
                        <SelectItem value="amendments">Amendments</SelectItem>
                        <SelectItem value="minutes">Meeting Minutes</SelectItem>
                        <SelectItem value="legal">Legal Notices</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Version</Label>
                      <Input
                        value={documentForm.version}
                        onChange={(e) => setDocumentForm({ ...documentForm, version: e.target.value })}
                        placeholder="1.0"
                      />
                    </div>
                    <div>
                      <Label>Access Level</Label>
                      <Select value={documentForm.access_level} onValueChange={(v) => setDocumentForm({ ...documentForm, access_level: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="public">Public</SelectItem>
                          <SelectItem value="owners_only">Owners Only</SelectItem>
                          <SelectItem value="board_only">Board Only</SelectItem>
                          <SelectItem value="attorney_privileged">Attorney Privileged</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={documentForm.description}
                      onChange={(e) => setDocumentForm({ ...documentForm, description: e.target.value })}
                      placeholder="Brief description of the document..."
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDocumentDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreateDocument} className="bg-primary hover:bg-primary/90">Create Document</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

      {/* Main Content */}
      <div className="mt-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5 bg-white border mb-6">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-primary data-[state=active]:text-white">Dashboard</TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-primary data-[state=active]:text-white">Documents</TabsTrigger>
            <TabsTrigger value="compliance" className="data-[state=active]:bg-primary data-[state=active]:text-white">Compliance</TabsTrigger>
            <TabsTrigger value="hearings" className="data-[state=active]:bg-primary data-[state=active]:text-white">Hearings</TabsTrigger>
            <TabsTrigger value="policies" className="data-[state=active]:bg-primary data-[state=active]:text-white">Policies</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{dashboard?.active_cases || 0}</p>
                      <p className="text-xs text-muted-foreground">Active Cases</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Gavel className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{dashboard?.upcoming_hearings?.length || 0}</p>
                      <p className="text-xs text-muted-foreground">Upcoming Hearings</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{dashboard?.governing_documents || 0}</p>
                      <p className="text-xs text-muted-foreground">Documents</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{dashboard?.pending_policies || 0}</p>
                      <p className="text-xs text-muted-foreground">Pending Policies</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Cases by Stage */}
            {isAdmin && dashboard?.cases_by_stage && (
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Compliance Cases by Stage</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(dashboard.cases_by_stage).map(([stage, count]) => (
                      <div key={stage} className="p-3 bg-gray-50 rounded-lg text-center">
                        <p className="text-2xl font-bold">{count}</p>
                        <p className="text-xs text-muted-foreground capitalize">{stage.replace(/_/g, " ")}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* AI Draft Tool */}
            {isAdmin && (
              <Card className="border-0 shadow-sm bg-gradient-to-br from-primary to-primary/80 text-white">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#B87333]" />
                    AI Notice Drafting
                  </CardTitle>
                  <CardDescription className="text-white/70">Generate professional legal notices with AI assistance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <Label className="text-white/80">Notice Type</Label>
                      <Select value={draftForm.notice_type} onValueChange={(v) => setDraftForm({ ...draftForm, notice_type: v })}>
                        <SelectTrigger className="bg-white/10 border-white/20 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="courtesy">Courtesy Notice</SelectItem>
                          <SelectItem value="formal">Formal Notice</SelectItem>
                          <SelectItem value="hearing">Hearing Notice</SelectItem>
                          <SelectItem value="fine">Fine Notice</SelectItem>
                          <SelectItem value="appeal_decision">Appeal Decision</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white/80">Owner Name</Label>
                      <Input
                        value={draftForm.owner_name}
                        onChange={(e) => setDraftForm({ ...draftForm, owner_name: e.target.value })}
                        placeholder="John Smith"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        onClick={handleAiDraftNotice}
                        disabled={aiLoading}
                        variant="secondary"
                        className="w-full"
                      >
                        {aiLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                        Generate Draft
                      </Button>
                    </div>
                  </div>
                  {aiDraft && (
                    <div className="p-4 bg-white/10 rounded-lg">
                      <pre className="text-sm text-white/90 whitespace-pre-wrap font-sans">{aiDraft}</pre>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.length > 0 ? documents.map((doc) => (
                <Card key={doc.document_id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        {getAccessIcon(doc.access_level)}
                        <CardTitle className="text-base">{doc.title}</CardTitle>
                      </div>
                      <Badge variant="outline" className="text-xs">v{doc.version}</Badge>
                    </div>
                    <Badge className="w-fit capitalize">{doc.category?.replace(/_/g, " ")}</Badge>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{doc.description || "No description"}</p>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>Effective: {formatDate(doc.effective_date) || "N/A"}</span>
                      {doc.is_current && <Badge className="bg-green-100 text-green-800">Current</Badge>}
                    </div>
                  </CardContent>
                </Card>
              )) : (
                <div className="col-span-full text-center py-12 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No governing documents found.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance" className="space-y-4">
            {cases.length > 0 ? cases.map((c) => (
              <Card key={c.case_id} className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getStageColor(c.stage)}>{c.stage?.replace(/_/g, " ")}</Badge>
                        {c.fine_amount > 0 && (
                          <Badge variant="outline" className="text-red-600">${c.fine_amount.toFixed(2)} fine</Badge>
                        )}
                      </div>
                      <p className="font-medium">Case #{c.case_id}</p>
                      <p className="text-sm text-muted-foreground">Notice: {c.violation?.category || "N/A"}</p>
                      <p className="text-sm text-muted-foreground">Owner: {c.owner?.name || "Unknown"}</p>
                    </div>
                    <div className="text-right">
                      {c.deadline && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Due: {formatDate(c.deadline)}
                        </p>
                      )}
                      {c.hearing_id && (
                        <Badge variant="outline" className="mt-2">Hearing Scheduled</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <div className="text-center py-12 text-muted-foreground">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-50 text-green-500" />
                <p>No active compliance cases.</p>
              </div>
            )}
          </TabsContent>

          {/* Hearings Tab */}
          <TabsContent value="hearings" className="space-y-4">
            {hearings.length > 0 ? hearings.map((h) => (
              <Card key={h.hearing_id} className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-purple-100 rounded-xl flex flex-col items-center justify-center">
                      <span className="text-xs font-medium text-purple-600">
                        {new Date(h.scheduled_date).toLocaleDateString("en-US", { month: "short" })}
                      </span>
                      <span className="text-xl font-bold text-purple-700">
                        {new Date(h.scheduled_date).getDate()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={h.status === "scheduled" ? "bg-blue-100 text-blue-800" : h.status === "decided" ? "bg-green-100 text-green-800" : "bg-gray-100"}>{h.status}</Badge>
                        {h.decision && <Badge variant="outline">{h.decision}</Badge>}
                      </div>
                      <p className="font-medium">Hearing for Case #{h.case_id}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {new Date(h.scheduled_date).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                        at {h.location}
                      </p>
                    </div>
                    {h.fine_assessed > 0 && (
                      <div className="text-right">
                        <p className="text-lg font-bold text-red-600">${h.fine_assessed.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground">Fine Assessed</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )) : (
              <div className="text-center py-12 text-muted-foreground">
                <Gavel className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hearings scheduled.</p>
              </div>
            )}
          </TabsContent>

          {/* Policies Tab */}
          <TabsContent value="policies" className="space-y-4">
            {policies.length > 0 ? policies.map((p) => (
              <Card key={p.policy_id} className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={p.status === "adopted" ? "bg-green-100 text-green-800" : p.status === "under_review" ? "bg-amber-100 text-amber-800" : "bg-gray-100"}>{p.status?.replace(/_/g, " ")}</Badge>
                        <Badge variant="outline">{p.category}</Badge>
                      </div>
                      <p className="font-medium">{p.title}</p>
                      <p className="text-sm text-muted-foreground mt-1">{p.description}</p>
                    </div>
                    <div className="text-right text-sm text-muted-foreground">
                      {p.effective_date && <p>Effective: {formatDate(p.effective_date)}</p>}
                      {p.votes && Object.keys(p.votes).length > 0 && (
                        <p className="mt-1">{Object.values(p.votes).filter(v => v === "approve").length} approvals</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <div className="text-center py-12 text-muted-foreground">
                <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No policy proposals.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );

  return <AdminSidebar>{pageContent}</AdminSidebar>;
};

export default LegalCompliancePage;
